import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SortingRule } from 'react-table'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { CommonButton } from 'components/Button'
import { getSortOptions } from 'components/Table/utils'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import BillingHistoryTable from './BillingHistoryTable'
import { BILLING_HISTORY_HEADERS } from './consts'
import { billingHistoryMessages } from './messages'
import {
  billingChargesHasMoreSelector,
  billingChargesListSelector,
  billingChargesSelector,
} from './selectors'
import {
  billingChargesListActions,
  fetchBillingChargesThunk,
  getBillingChargesActions,
} from './slice'
import {
  BillingHistoryHeading,
  BillingHistoryLoadWrap,
  BillingHistoryPageWrapper,
} from './styles'
import type { BillingChargeType } from './types'
import { commonMessages } from 'app/i18n/messages'
import { SpinnerCentered } from 'components/Loader'

function BillinHistoryPage() {
  const { formatMessage } = useIntl()

  const dispatch = useAppDispatch()
  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const chargesList = useAppSelector<BillingChargeType[]>(
    billingChargesListSelector
  )
  const hasMore = useAppSelector<boolean>(billingChargesHasMoreSelector)
  const isFetchingGetCharges = useAppSelector(billingChargesSelector.isFetching)
  const isFinishedGetCharges = useAppSelector(billingChargesSelector.isFinished)

  const chargesLoaded = isFinishedGetCharges && !!chargesList

  const [recentChargeId, setRecentChargeId] = useState('')

  const fetchData = () => {
    setRecentChargeId(chargesList[chargesList.length - 1]?.id)
  }
  const getBillingHistory = useCallback(() => {
    dispatch(fetchBillingChargesThunk(currentRootId || '', recentChargeId))
  }, [currentRootId, dispatch, recentChargeId])

  useEffect(() => {
    if (currentRootId) {
      dispatch(billingChargesListActions.init())
    }
  }, [currentRootId, dispatch])

  useEffect(() => {
    getBillingHistory()
  }, [dispatch, getBillingHistory, recentChargeId])

  useEffect(() => {
    return () => {
      dispatch(billingChargesListActions.init())
      dispatch(getBillingChargesActions.clear())
    }
  }, [dispatch])

  const headers = useMemo(
    () => ({
      date: {
        label: formatMessage(billingHistoryMessages.date),
        id: BILLING_HISTORY_HEADERS.DATE,
        desc: true,
      },
      homeName: {
        label: formatMessage(billingHistoryMessages.homeName),
        id: BILLING_HISTORY_HEADERS.HOME_NAME,
      },
      description: {
        label: formatMessage(billingHistoryMessages.description),
        id: BILLING_HISTORY_HEADERS.DESCRIPTION,
      },
      paymentMethod: {
        label: formatMessage(billingHistoryMessages.paymentMethod),
        id: BILLING_HISTORY_HEADERS.PAYMENT_METHOD,
      },
      amount: {
        label: formatMessage(billingHistoryMessages.amount),
        id: BILLING_HISTORY_HEADERS.AMOUNT,
      },
      status: {
        label: ' ',
        id: BILLING_HISTORY_HEADERS.STATUS,
      },
    }),
    [formatMessage]
  )

  const sortOptions = useMemo(() => getSortOptions(headers), [headers])
  const [, setSortBy] = useState<SortingRule<BillingChargeType>>(sortOptions[0])

  return (
    <BillingHistoryPageWrapper>
      <BillingHistoryHeading>
        <FormattedMessage {...billingHistoryMessages.title} />
      </BillingHistoryHeading>
      {chargesLoaded && (
        <BillingHistoryTable
          data={chargesList}
          headers={headers}
          defaultSortBy={sortOptions[0]}
          sortBy={sortOptions[0]}
          setSortBy={setSortBy}
        />
      )}
      {isFetchingGetCharges && <SpinnerCentered />}
      <BillingHistoryLoadWrap>
        {hasMore && (
          <CommonButton
            type="button"
            variant="secondary"
            onClick={fetchData}
            disabled={isFetchingGetCharges}
          >
            <FormattedMessage {...commonMessages.showMore} />
          </CommonButton>
        )}
      </BillingHistoryLoadWrap>
    </BillingHistoryPageWrapper>
  )
}

export default BillinHistoryPage
